import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, ChevronRight, Star, Clock, Gift, Calendar, MapPin, Coffee, UtensilsCrossed, Sparkles, Shirt, ArrowRight } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-8 h-8 text-[10px]" : "w-10 h-10 text-xs";
  return <div className={`${s} rounded-xl ${colors[i]} flex items-center justify-center flex-shrink-0`}><span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

const hourLabels = [
  { range: [6, 10], label: "Morning", icon: Coffee, color: "bg-amber-500" },
  { range: [11, 14], label: "Lunch", icon: UtensilsCrossed, color: "bg-orange-500" },
  { range: [15, 17], label: "Afternoon", icon: Gift, color: "bg-sky-500" },
  { range: [18, 21], label: "Dinner", icon: UtensilsCrossed, color: "bg-indigo-500" },
  { range: [22, 23], label: "Late Night", icon: Sparkles, color: "bg-purple-500" },
];

const getTimeSlot = (category) => {
  const map = {
    "Cafes & Drinks": 0, "Bakery": 0,
    "Fast Food": 1, "Restaurant": 3,
    "Clothing & Apparel": 2, "Retail": 2, "Beauty & Skincare": 2,
    "Dessert & Ice Cream": 2, "Entertainment": 2,
    "Grocery": 1,
  };
  return map[category] ?? 2;
};

export const PlannerView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, stats, favoriteIds, toggleFavorite, bestValue } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);
  const [expandedSlot, setExpandedSlot] = useState(null);

  // Build "day planner" from offers
  const slots = useMemo(() => {
    const result = hourLabels.map((h, idx) => ({
      ...h,
      idx,
      offers: []
    }));
    offers.forEach(o => {
      const slotIdx = getTimeSlot(o.category);
      result[slotIdx].offers.push(o);
    });
    return result;
  }, [offers]);

  // Today's top 3 — quick action
  const todaysPicks = useMemo(() => {
    const picks = [];
    if (expiringSoon[0]) picks.push(expiringSoon[0]);
    if (bestValue[0] && !picks.find(p => p.business_id === bestValue[0].business_id)) picks.push(bestValue[0]);
    const easy = offers.find(o => o.ease_stars >= 4 && !picks.find(p => p.business_id === o.business_id));
    if (easy) picks.push(easy);
    return picks;
  }, [offers, expiringSoon, bestValue]);

  return (
    <div className="min-h-screen bg-slate-50 pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Planner header */}
      <div className="bg-white px-6 pt-8 pb-5 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-xs font-medium uppercase tracking-widest">Birthday Planner</p>
            <h1 className="text-2xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your Big Day
            </h1>
          </div>
          <div className="bg-sky-500 text-white rounded-2xl px-4 py-2 text-center">
            <p className="text-xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>{stats.days_until_birthday}</p>
            <p className="text-[10px] text-sky-200">days left</p>
          </div>
        </div>

        {/* Value summary */}
        <div className="mt-4 flex items-center gap-4">
          <div className="flex-1 bg-green-50 rounded-xl py-2.5 px-3 text-center">
            <p className="text-lg font-bold text-green-600">${totalValue.toFixed(0)}</p>
            <p className="text-[10px] text-green-400">total value</p>
          </div>
          <div className="flex-1 bg-slate-50 rounded-xl py-2.5 px-3 text-center">
            <p className="text-lg font-bold text-slate-700">{offers.length}</p>
            <p className="text-[10px] text-slate-400">deals</p>
          </div>
          <div className="flex-1 bg-red-50 rounded-xl py-2.5 px-3 text-center">
            <p className="text-lg font-bold text-red-600">{expiringSoon.length}</p>
            <p className="text-[10px] text-red-400">expiring</p>
          </div>
        </div>
      </div>

      {/* Today's picks — priority action */}
      {todaysPicks.length > 0 && (
        <div className="px-6 mt-6">
          <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-3">Start Here</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide">
            {todaysPicks.map((offer, i) => (
              <motion.div
                key={offer.business_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => onOfferClick(offer)}
                className="flex-shrink-0 w-56 bg-white rounded-2xl p-4 border border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all"
                data-testid={`planner-pick-${offer.business_id}`}
              >
                <div className="flex items-center gap-3">
                  <MiniLogo name={offer.name} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">{offer.name}</p>
                    {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                    <p className="text-xs text-slate-400 truncate">{offer.offer_title}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-50">
                  <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= offer.ease_stars ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Day planner timeline */}
      <div className="px-6 mt-6">
        <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-4">Your Birthday Schedule</h2>

        <div className="space-y-3">
          {slots.map((slot) => {
            const isExpanded = expandedSlot === slot.idx;
            const displayOffers = isExpanded ? slot.offers : slot.offers.slice(0, 2);
            if (slot.offers.length === 0) return null;

            return (
              <motion.div
                key={slot.idx}
                layout
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setExpandedSlot(isExpanded ? null : slot.idx)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                  data-testid={`planner-slot-${slot.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${slot.color} flex items-center justify-center`}>
                    <slot.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800 text-sm">{slot.label}</p>
                    <p className="text-xs text-slate-400">{slot.range[0] > 12 ? slot.range[0] - 12 : slot.range[0]}:00 {slot.range[0] >= 12 ? "PM" : "AM"} - {slot.range[1] > 12 ? slot.range[1] - 12 : slot.range[1]}:00 {slot.range[1] >= 12 ? "PM" : "AM"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-700">{slot.offers.length}</p>
                    <p className="text-[10px] text-slate-400">offers</p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-slate-300 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </button>

                <AnimatePresence>
                  {(isExpanded || slot.offers.length <= 2) && (
                    <motion.div
                      initial={isExpanded ? { height: 0, opacity: 0 } : false}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 space-y-2">
                        {displayOffers.map(offer => (
                          <div
                            key={offer.business_id}
                            onClick={() => onOfferClick(offer)}
                            className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                            data-testid={`planner-offer-${offer.business_id}`}
                          >
                            <MiniLogo name={offer.name} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{offer.name}</p>
                              <p className="text-xs text-slate-400 truncate">{offer.category}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                              <button onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }} className="p-0.5">
                                <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-200"}`} />
                              </button>
                            </div>
                          </div>
                        ))}
                        {!isExpanded && slot.offers.length > 2 && (
                          <p className="text-xs text-sky-500 font-medium text-center pt-1">+ {slot.offers.length - 2} more</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
